// Live run chart: four stacked per-corner uPlot lanes that draw a run's damper
// positions as the simulation advances. Each lane shows the recorded ghost
// (dashed, full width from the start) and the live trace filling in on top
// (solid). Mirrors MultiRunOverlayChart's lane pattern and joins the same
// "damper-overlay" cursor-sync group so hovering syncs across every damper
// chart on the page.
//
// uPlot lifecycle (load-bearing):
//   - the plot is created ONCE per run (keyed on runId + durationS + whether a
//     ghost is present), never per poll;
//   - the x-scale is fixed to [0, durationS] up front via a range function so
//     the trace draws left-to-right into a stable window and setData never
//     resets it;
//   - the y-range comes from the ghost extent when a ghost is present;
//   - appends go through setData only.
import { useEffect, useRef } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";

import type {
  LiveChannelArrays,
  LiveGhost,
  LiveR2,
  LiveWaveformStatus,
} from "../api/liveWaveform";

const CORNERS = ["lf", "rf", "lr", "rr"] as const;
const CORNER_LABEL = { lf: "LF", rf: "RF", lr: "LR", rr: "RR" } as const;
type CornerKey = (typeof CORNERS)[number];

const GHOST_COLOUR = "#777";
const LIVE_COLOUR = "rgba(74, 144, 217, 0.95)";
const Y_PADDING_FRAC = 0.1;

// Resample a live prefix onto a (full-width) ghost time grid. For each grid
// point at or before the live frontier, linearly interpolate the live value;
// beyond the frontier the point is null (nothing drawn there yet). This lets a
// live trace at its own decimated sampling share one x-axis with a ghost of a
// different sampling (the R0-baseline fallback), which uPlot requires.
export function resampleOntoGrid(
  srcT: number[],
  srcY: number[],
  gridT: number[],
): (number | null)[] {
  const out: (number | null)[] = new Array(gridT.length).fill(null);
  const n = srcT.length;
  if (n === 0) return out;
  const frontier = srcT[n - 1];
  let j = 0;
  for (let i = 0; i < gridT.length; i++) {
    const tg = gridT[i];
    if (tg > frontier) break; // grid is ascending: everything after is null
    if (tg <= srcT[0]) {
      out[i] = srcY[0];
      continue;
    }
    while (j < n - 1 && srcT[j + 1] < tg) j++;
    const t0 = srcT[j];
    const t1 = srcT[j + 1] ?? t0;
    const y0 = srcY[j];
    const y1 = srcY[j + 1] ?? y0;
    out[i] = t1 === t0 ? y0 : y0 + (y1 - y0) * ((tg - t0) / (t1 - t0));
  }
  return out;
}

function paddedExtent(arr: number[]): [number, number] {
  let lo = Number.POSITIVE_INFINITY;
  let hi = Number.NEGATIVE_INFINITY;
  for (const v of arr) {
    if (!Number.isFinite(v)) continue;
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return [0, 1];
  const span = hi - lo;
  const pad = span === 0 ? Math.abs(lo) * Y_PADDING_FRAC || 0.01 : span * Y_PADDING_FRAC;
  return [lo - pad, hi + pad];
}

export type LiveRunChartProps = {
  runId: number;
  runNumber: number;
  durationS: number;
  timeS: number[]; // seconds, live prefix
  channels: LiveChannelArrays; // mm, live prefix
  ghost: LiveGhost | null; // mm, full width
  // Append signal: changes on every folded cursor. The underlying arrays are
  // mutated in place, so this scalar is the effect dependency, not the arrays.
  sampleCount: number;
  simTimeS: number;
  status: LiveWaveformStatus;
  liveR2: LiveR2 | null;
};

type Lane = { data: uPlot.AlignedData; yRange: [number, number] | null };

function buildLane(
  corner: CornerKey,
  timeS: number[],
  channels: LiveChannelArrays,
  ghost: LiveGhost | null,
): Lane {
  const liveY = channels[corner];
  if (ghost) {
    const gx = ghost.timeS;
    const gy = ghost.channels[corner];
    const liveOnGrid = resampleOntoGrid(timeS, liveY, gx);
    return {
      data: [gx, gy, liveOnGrid] as unknown as uPlot.AlignedData,
      yRange: paddedExtent(gy),
    };
  }
  // No ghost: draw the live trace against its own (growing) time axis.
  return {
    data: [timeS, liveY] as unknown as uPlot.AlignedData,
    yRange: null,
  };
}

function buildLaneOpts(
  corner: CornerKey,
  durationS: number,
  hasGhost: boolean,
  yRange: [number, number] | null,
): uPlot.Options {
  const series: uPlot.Series[] = [{} as uPlot.Series];
  if (hasGhost) {
    series.push({
      label: "recorded (ghost)",
      stroke: GHOST_COLOUR,
      width: 1,
      dash: [4, 4],
    } as uPlot.Series);
  }
  series.push({
    label: "live",
    stroke: LIVE_COLOUR,
    width: 1.6,
  } as uPlot.Series);
  return {
    title: CORNER_LABEL[corner],
    width: 800,
    height: 130,
    series,
    cursor: { sync: { key: "damper-overlay" } },
    scales: {
      x: { time: false, range: () => [0, durationS] },
      y: yRange ? { range: () => yRange } : {},
    },
    axes: [{}, {}],
  };
}

export function LiveRunChart(props: LiveRunChartProps) {
  const { runId, runNumber, durationS, timeS, channels, ghost, sampleCount } = props;
  const { simTimeS, status, liveR2 } = props;

  const lfRef = useRef<HTMLDivElement>(null);
  const rfRef = useRef<HTMLDivElement>(null);
  const lrRef = useRef<HTMLDivElement>(null);
  const rrRef = useRef<HTMLDivElement>(null);
  const containerRefs: Record<CornerKey, React.RefObject<HTMLDivElement>> = {
    lf: lfRef,
    rf: rfRef,
    lr: lrRef,
    rr: rrRef,
  };
  const plotRefs = useRef<Record<CornerKey, uPlot | null>>({
    lf: null,
    rf: null,
    lr: null,
    rr: null,
  });
  // Latest data, read by both effects without forcing the mount effect to
  // re-run when array identity changes.
  const latest = useRef({ timeS, channels, ghost });
  latest.current = { timeS, channels, ghost };

  // Mount + cleanup. Created once per run: keyed on runId, durationS and
  // ghost presence, all stable within a run.
  useEffect(() => {
    const cur = latest.current;
    for (const corner of CORNERS) {
      const root = containerRefs[corner].current;
      if (!root) continue;
      const lane = buildLane(corner, cur.timeS, cur.channels, cur.ghost);
      const opts = buildLaneOpts(corner, durationS, cur.ghost != null, lane.yRange);
      plotRefs.current[corner] = new uPlot(opts, lane.data, root);
    }
    return () => {
      for (const corner of CORNERS) {
        plotRefs.current[corner]?.destroy();
        plotRefs.current[corner] = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, durationS, ghost != null]);

  // Append: on every folded cursor, setData with the grown arrays. The x-scale
  // stays pinned to [0, durationS] via the range function, so the window does
  // not reset.
  useEffect(() => {
    const cur = latest.current;
    for (const corner of CORNERS) {
      const p = plotRefs.current[corner];
      if (!p) continue;
      const lane = buildLane(corner, cur.timeS, cur.channels, cur.ghost);
      p.setData(lane.data, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleCount]);

  const pct =
    durationS > 0
      ? Math.max(0, Math.min(100, (simTimeS / durationS) * 100))
      : 0;

  return (
    <div className="live-run-chart" data-testid="live-run-chart">
      <div className="live-run-head" data-testid="live-run-header">
        <span className="live-run-title">
          R{runNumber} · simulating {simTimeS.toFixed(1)} / {durationS.toFixed(1)} s
        </span>
        <span className="muted live-run-status">{status}</span>
      </div>
      <div
        className="live-run-progress"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={Math.round(durationS)}
        aria-valuenow={Math.round(simTimeS)}
        data-testid="live-run-progress"
      >
        <div className="live-run-progress-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="live-run-r2-row" data-testid="live-run-r2">
        {CORNERS.map((c) => {
          const v = liveR2?.[c];
          return (
            <span className="live-r2-chip" key={c} data-testid={`live-r2-${c}`}>
              {CORNER_LABEL[c]}{" "}
              <b>{v != null ? v.toFixed(2) : ""}</b>
            </span>
          );
        })}
        <span className="muted live-r2-caption">live R² (advisory)</span>
      </div>
      <div className="live-run-lanes">
        <div className="live-run-lane" ref={lfRef} />
        <div className="live-run-lane" ref={rfRef} />
        <div className="live-run-lane" ref={lrRef} />
        <div className="live-run-lane" ref={rrRef} />
      </div>
    </div>
  );
}
